import * as React from "react";
import * as ReactDOM from "react-dom";
import * as marked from "marked";
import * as moment from "moment";
import { CalendarViewMode } from "../../enumerations";
import * as apiAccounts from "../../api/accounts";
import { getGridChildStyle } from "../../utils/utils";
import { IUserNotification } from "../../models/get";
import { Flyout } from "../flyout";
import { Loader, LoaderStyle } from "../loader";
import { Alert, AlertType } from "../alert";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../toolbar";
import { ScrollViewer, ScrollBarVisibility } from "../scrollViewer";
import { Notification } from "./notification";

interface IProperties
{
    /**
     * Die vorherige oder momentane Route. Wenn der Dialog als Modal geöffnet wird,
     * ist hier die vorherige Route drin.
     */
    route: () => uri.URI,

    /**
     * Das react-router history object.
     */
    history: any,

    /**
     * Ein Wert der angibt ob das Flyout geöffnet ist.
     */
    isOpen: boolean,

    /**
     * Wird ausgeführt, sobald sich die Anzahl der Benachrichtigungen ändert.
     */
    onNotificationsChange?: () => void,

    /**
     * Wird ausgeführt, sobald auf den Close Button oder außerhalb des Flyouts geklickt wird.
     */
    onClose?: () => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Enthält alle Benachrichtigungen des angemeldeten Benutzers.
     */
    notifications?: IUserNotification[]
}

export class NotificationsFlyout extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see NotificationsFlyout.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);
        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Wird ausgeführt, sobald die Komponente neue Eigenschaften erhält.
     * Hier werden die benötigten Daten erneut geladen.
     * @param nextProps Die nächsten/neuen Eigenschaften.
     */
    componentWillReceiveProps(nextProps: IProperties)
    {
        const oldProp = this.props;
        const newProp = nextProps;

        if (newProp.isOpen)
            this.initAsync(nextProps);
    }

    /**
     * Lädt alle Benachrichtigungen des momentan angemeldeten Benutzers.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var notifications = await apiAccounts.getCurrentUsersNotifications();

        this.setState({
            isLoading: false,
            notifications: notifications
        });
    }

    /**
     * Entfernt alle schon gelesenen Benachrichtigungen.
     */
    private onRemoveAllReadNotificationsClick = (): void =>
    {
        apiAccounts.deleteReadNotifications().then(() =>
        {
            const { onNotificationsChange } = this.props;

            this.initAsync(this.props);
            onNotificationsChange && onNotificationsChange();
        });
    }

    /**
     * Entfernt alle Benachrichtigungen.
     */
    private onRemoveAllNotificationsClick = (): void =>
    {
        apiAccounts.deleteAllNotifications().then(() =>
        {
            const { onNotificationsChange } = this.props;

            this.initAsync(this.props);
            onNotificationsChange && onNotificationsChange();
        });
    }

    /**
     * Öffnet den Termin Dialog sobald auf eine Benachrichtigung doppel geklickt wird.
     * @param notification Die Benachrichtigung die geöffnet werden soll.
     */
    private onNotificationDoubleClick = (notification: IUserNotification): void =>
    {
        const { route, history } = this.props;
        const { event_id, calendar_id } = notification;
        const uri: uri.URI = route();
        const viewMode: CalendarViewMode = uri.getViewMode();

        if (viewMode === CalendarViewMode.planning)
        {
            history.push("/event/" + event_id.toString() + "?calendar=" + calendar_id.toString(), {
                modal: true
            });
        }
        else
        {
            history.push("/event/" + event_id.toString(), {
                modal: true
            });
        }

        // Benachrichtigung als gelesen markieren.
        apiAccounts.markNotificationAsRead(notification.id).then(() =>
        {
            const { onNotificationsChange } = this.props;

            this.initAsync(this.props);
            onNotificationsChange && onNotificationsChange();
        });
    }

    /**
     * Entfernt die Benachrichtigung aus der Datenbank.
     * @param notification Die Benachrichtigung die entfernt werden soll.
     */
    private onNotificationRemoveClick = (notification: IUserNotification): void =>
    {
        const { route, history } = this.props;

        // Benachrichtigung als gelesen markieren.
        apiAccounts.deleteNotification(notification.id).then(() =>
        {
            const { onNotificationsChange } = this.props;

            this.initAsync(this.props);
            onNotificationsChange && onNotificationsChange();
        });
    }

    /**
     * Rendert den Content Bereich des Flyouts.
     */
    private renderContent(): JSX.Element
    {
        const { isLoading, notifications } = this.state;

        if (isLoading)
        {
            return (
                <div className="fo-notifications">
                    <Loader loaderStyle={LoaderStyle.style1} />
                </div>
            );
        }
        else if (notifications == null || notifications.length === 0)
        {
            return (
                <div className="fo-notifications">
                    <div className="fo-notifications_empty">
                        In dieser Sitzung gibt es keine neuen <br /> Benachrichtigungen.
                    </div>
                </div>
            );
        }
        else
        {
            return (
                <div className="fo-notifications">
                    <ToolBar
                        style={getGridChildStyle(1, 1, 1, 1)}>
                        <ToolBarItem
                            icon="close"
                            text="Gelesene entfernen"
                            onClick={this.onRemoveAllReadNotificationsClick} />
                        <ToolBarItem
                            icon="close"
                            text="Alle entfernen"
                            onClick={this.onRemoveAllNotificationsClick} />
                    </ToolBar>
                    <div
                        style={{
                            position: "relative",
                            ...getGridChildStyle(1, 1, 2, 2)
                        }}>
                        <ScrollViewer
                            HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                            VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                            {
                                notifications.map((notification) =>
                                {
                                    return (
                                        <Notification
                                            key={notification.id}
                                            notification={notification}
                                            onDoubleClick={this.onNotificationDoubleClick}
                                            onRemove={this.onNotificationRemoveClick} />
                                    );
                                })
                            }
                        </ScrollViewer>
                    </div>
                </div>
            );
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isOpen, onClose } = this.props;
        const { isLoading, notifications } = this.state;
        
        return (
            <Flyout
                headerText="Benachrichtigungen"
                width={420}
                alignRight
                isOpen={isOpen}
                onClose={onClose}>
                {this.renderContent()}
            </Flyout>
        );
    }
}